import type { EngineEvent } from '../../interfaces/websocket-events.interface'
import {
  addAgentReportEntity,
  addSubAgentChatMessage,
  appendToItemStream,
  startDelegation,
} from '../slices/chat.slice'
import type { AgentReportFormat } from '../slices/chat-message-entity.interface'
import { asString, asStringOrUndefined, extractIds } from './engine-event-primitives.helper'
import {
  isActiveConversation,
  isEntityCreated,
  type WebSocketStoreApi,
} from './websocket-store.helper'

interface DelegationTarget {
  slug: string
  label: string
}

const extractDelegationTarget = (event: EngineEvent): DelegationTarget => {
  const slug = asString(event.data.target_node_slug)
  return { slug, label: asString(event.data.target_label) || slug }
}

const subAgentMessageId = (delegationId: string): string => `del-${delegationId}-msg`

export const handleDelegationStarted = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const ids = extractIds(event)
  if (!ids) return
  if (!isActiveConversation(store, ids.conversationId)) return

  const target = extractDelegationTarget(event)
  const supervisorSlug = asStringOrUndefined(event.agent_node_slug) ?? ''
  const supervisorLabel = asString(event.data.source_label) || supervisorSlug
  store.dispatch(
    startDelegation({
      itemId: ids.messageId,
      eventId: event.event_id,
      conversationId: ids.conversationId,
      supervisorId: supervisorLabel,
      targetList: target.slug ? [target.slug] : [],
      targetLabelList: target.label ? [target.label] : [],
      task: asStringOrUndefined(event.data.task),
    })
  )
}

export const handleDelegationSynthesisDelta = (
  store: WebSocketStoreApi,
  event: EngineEvent
): void => {
  const ids = extractIds(event)
  if (!ids) return
  if (!isActiveConversation(store, ids.conversationId)) return
  const delegationId = asString(event.data.delegation_id)
  const delta = asString(event.data.delta)
  if (!delegationId || !delta) return

  const messageEntityId = subAgentMessageId(delegationId)
  if (!isEntityCreated(store, messageEntityId)) {
    const targetSlug = asString(event.data.target_node_slug)
    const targetLabel = asString(event.data.target_label) || targetSlug
    store.dispatch(
      addSubAgentChatMessage({
        conversationId: ids.conversationId,
        parentEventId: ids.messageId,
        eventId: messageEntityId,
        agentSlug: targetSlug || delegationId,
        agentLabel: targetLabel || 'Agent',
        content: '',
        turnStartedAt: event.ts,
        isStreaming: true,
      })
    )
  }
  store.dispatch(appendToItemStream({ itemId: messageEntityId, content: delta }))
}

interface DelegationFinishedContext {
  conversationId: string
  messageId: string
  eventId: string
  ts: string
  target: DelegationTarget
  reportArtifactId: string
  reportFormat: AgentReportFormat
  delegationId: string
}

const dispatchSubAgentChatBubble = (
  store: WebSocketStoreApi,
  ctx: DelegationFinishedContext,
  synthesis: string
): void => {
  if (!synthesis) return
  const messageId = ctx.delegationId
    ? subAgentMessageId(ctx.delegationId)
    : `${ctx.eventId}::message`
  store.dispatch(
    addSubAgentChatMessage({
      conversationId: ctx.conversationId,
      parentEventId: ctx.messageId,
      eventId: messageId,
      agentSlug: ctx.target.slug || ctx.eventId,
      agentLabel: ctx.target.label || 'Agent',
      content: synthesis,
      reportArtifactId: ctx.reportArtifactId || undefined,
      turnStartedAt: ctx.ts,
    })
  )
}

const dispatchSubAgentReportCard = (
  store: WebSocketStoreApi,
  ctx: DelegationFinishedContext,
  cardContent: string
): void => {
  if (!cardContent) return
  const cardId = ctx.reportArtifactId || ctx.eventId
  store.dispatch(
    addAgentReportEntity({
      conversationId: ctx.conversationId,
      parentEventId: ctx.messageId,
      agentId: ctx.target.slug || ctx.eventId,
      agentName: ctx.target.label || 'Agent',
      reportContent: cardContent,
      eventId: cardId,
      reportVersion: 1,
      reportId: cardId,
      isMasterArtifact: false,
      reportFormat: ctx.reportFormat,
      turnStartedAt: ctx.ts,
    })
  )
}

export const handleDelegationFinished = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const ids = extractIds(event)
  if (!ids) return
  if (!isActiveConversation(store, ids.conversationId)) return

  const ctx: DelegationFinishedContext = {
    conversationId: ids.conversationId,
    messageId: ids.messageId,
    eventId: event.event_id,
    ts: event.ts,
    target: extractDelegationTarget(event),
    reportArtifactId: asString(event.data.report_artifact_id),
    reportFormat: asString(event.data.report_format) === 'markdown' ? 'markdown' : 'html',
    delegationId: asString(event.data.delegation_id),
  }
  dispatchSubAgentChatBubble(store, ctx, asString(event.data.synthesis))
  dispatchSubAgentReportCard(
    store,
    ctx,
    asString(event.data.report_html) || asString(event.data.report)
  )
}
